import { faCommentsAlt } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import { Redirect } from 'react-router-dom';
import { ProjectConversationId, ProjectId } from 'api/models';
import {
  EmptyState,
  EmptyStateContainer,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Resizable } from 'components/atoms/resizable';
import {
  Content,
  Layout,
  Sider,
} from 'components/pages/project-details/project-spaces/project-spaces.styles';
import { useProjectDetail } from 'data/projects';
import {
  ProjectResourceOrUndefined,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useIsExternalUser } from 'hooks/use-user-type';
import { useProjectGlobalConversationQuery } from 'queries/project-global-conversation/project-global-conversation';
import {
  getProjectConversationDetailUrl,
  getProjectConversationPageUrl,
} from 'utils/urls';
import {
  ConversationNameList,
  NewConversationAction,
  NewConversationAddMembersModal,
  ProjectConversationPane,
  ProjectConversationSkelton,
} from './conversation-components';
import {
  getGlobalConversationEntityMetaFromUrl,
  useActiveProjectConversation,
  useProjectConversationsListUpdates,
} from './proj-conversation-hooks';

const AddMembersModal = ({ isVisible, onCancel }) => {
  const activeConversation = useActiveProjectConversation();

  return (
    <NewConversationAddMembersModal
      conversationName={activeConversation?.conversationName ?? ''}
      isVisible={isVisible}
      onCancel={onCancel}
    />
  );
};

export const ProjectConversation: FC<{
  projectId: ProjectId;
}> = ({ projectId }) => {
  const { chatId } = useParams<{ chatId: string }>();
  const { entityId, entityType } =
    getGlobalConversationEntityMetaFromUrl(chatId) || {};

  const { hasAccessToResource } = useWithAccess();
  const { data: projectData } = useProjectDetail(projectId);
  const resourceData = ProjectResourceOrUndefined(projectData);
  const hasAccessToCreateConversation = hasAccessToResource({
    rule: 'CONVERSATION:CREATE',
    resourceData,
  });
  const isExternalUser = useIsExternalUser();

  // for resizable siders
  const [width, setWidth] = useState(253);
  const [isResizing, setIsResizing] = useState(false);

  const { conversations = [], isConversationsLoading } =
    useProjectConversationsListUpdates(projectId);
  const { data: globalConversations, isLoading: isGlobalConversationLoading } =
    useProjectGlobalConversationQuery(projectId);

  const isUserMemberOfChannel = (conv) => conv && conv.partOfConversation;

  const myConversations = conversations.filter(isUserMemberOfChannel);
  const otherConversations = conversations.filter(
    (conv) => !isUserMemberOfChannel(conv),
  );

  const [isAddMembersModalVisible, setAddMembersModalVisibility] =
    useState<boolean>(false);

  if (isConversationsLoading || isGlobalConversationLoading) {
    return <ProjectConversationSkelton />;
  }

  if (!chatId && conversations.length) {
    let convId: number | undefined;
    const myPublicConvo = myConversations.find((convo) => !convo.private);
    const anyPublicConvo = conversations.find((convo) => !convo.private);

    if (myPublicConvo) {
      convId = myPublicConvo.conversationId;
    } else if (anyPublicConvo) {
      convId = anyPublicConvo.conversationId;
    } else if (myConversations.length) {
      convId = myConversations[0].conversationId;
    } else if (conversations.length) {
      convId = conversations[0].conversationId;
    }

    if (convId) {
      return (
        <Redirect
          to={{
            pathname: getProjectConversationDetailUrl(
              projectId,
              ProjectConversationId(convId),
            ),
          }}
        />
      );
    }
  }
  if (conversations.length) {
    const isChatIdValid =
      conversations.find((conv) => conv.conversationId === Number(chatId)) ||
      (entityId && entityType);

    if (!isChatIdValid) {
      return (
        <Redirect
          to={{
            pathname: getProjectConversationPageUrl(projectId),
          }}
        />
      );
    }
  }
  const onNewConversationCreationsSuccess = () => {
    setAddMembersModalVisibility(true);
  };

  return (
    <Layout>
      {conversations.length > 0 && (
        <AddMembersModal
          isVisible={isAddMembersModalVisible}
          onCancel={() => setAddMembersModalVisibility(false)}
        />
      )}
      <Resizable
        initialWidth={253}
        minWidth={253}
        maxWidth={600}
        onChange={(w) => setWidth(w)}
        offset={isExternalUser ? 255 : 48}
        onResizeStart={() => setIsResizing(true)}
        onResizeStop={() => setIsResizing(false)}
      >
        <Sider
          data-test-id="projects.conversations.sidebar"
          width={width}
          isResizing={isResizing}
        >
          {hasAccessToCreateConversation ? (
            <NewConversationAction
              onSuccess={onNewConversationCreationsSuccess}
            />
          ) : null}
          <ConversationNameList
            myConversations={myConversations}
            otherConversations={otherConversations}
            projectId={projectId}
            conversationId={ProjectConversationId(Number(chatId))}
            globalConversations={globalConversations?.conversations}
          />
        </Sider>
      </Resizable>
      <Content>
        {conversations.length === 0 &&
        globalConversations?.conversations.length === 0 ? (
          <EmptyStateContainer>
            <EmptyState
              image={<EmptyStateIcon icon={faCommentsAlt} />}
              title={
                <FormattedMessage id="projects.conversations.new.empty.title" />
              }
              message={
                <>
                  <FormattedMessage id="projects.conversations.new.empty.message" />
                  {hasAccessToCreateConversation ? (
                    <NewConversationAction
                      onSuccess={onNewConversationCreationsSuccess}
                    />
                  ) : null}
                </>
              }
            />
          </EmptyStateContainer>
        ) : (
          <ProjectConversationPane projectId={projectId} />
        )}
      </Content>
    </Layout>
  );
};
